import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { EXPORTFORMAT_BESCHRIFTUNG } from '@mcikm-vote/shared';
import type { AbstimmungExportstand, ExportEintrag, ExportformatWert } from '@mcikm-vote/shared';

import { useAbstimmungen } from '../../admin/abstimmungenApi';
import {
  useAbstimmungExportieren,
  useExportLoeschen,
  useExportstaende,
  useVeranstaltungExporte,
  useVeranstaltungExportieren,
  useVeranstaltungPdfExportieren,
} from '../../admin/exporteApi';
import { useVeranstaltungen } from '../../admin/veranstaltungenApi';
import { BestaetigungsDialog } from '../../components/BestaetigungsDialog';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { ExportFormatZeile } from '../../components/ExportFormatZeile';
import { Karte } from '../../components/Karte';

/**
 * Ergebnisse und Exporte (§20.1, §27).
 *
 * Gezeigt werden ausschließlich **geschlossene** Abstimmungen: Nur sie haben ein Ergebnis, und nur
 * sie sind exportierbar (§27.1). Eine annullierte Abstimmung erhält nach §17.4 keines, eine
 * geöffnete dürfte keines zeigen (§16).
 *
 * Ein fehlender Export ist ein **Hinweis, keine Sperre** — §27.3 stellt ausdrücklich klar, dass er
 * weder die nächste Abstimmung noch das Veranstaltungsende blockiert.
 */

export function ErgebnisseSeite(): ReactElement {
  const veranstaltungen = useVeranstaltungen(true);
  const [gewaehlteId, setGewaehlteId] = useState('');
  const [zuLoeschen, setZuLoeschen] = useState<ExportEintrag | null>(null);

  const alle = veranstaltungen.data ?? [];
  // Ohne ausdrückliche Wahl die aktive Veranstaltung, sonst die zuletzt angelegte: Während einer
  // Sitzung ist die aktive gemeint, danach die eben beendete (§6.3).
  const vorgabe =
    alle.find((eintrag) => eintrag.status === 'freigegeben' || eintrag.status === 'laufend') ??
    alle[0];
  const veranstaltungId = gewaehlteId !== '' ? gewaehlteId : (vorgabe?.id ?? '');

  const abstimmungen = useAbstimmungen(veranstaltungId);
  const staende = useExportstaende(veranstaltungId);
  const exporte = useVeranstaltungExporte(veranstaltungId);

  const abstimmungExportieren = useAbstimmungExportieren();
  const veranstaltungExportieren = useVeranstaltungExportieren();
  const veranstaltungPdfExportieren = useVeranstaltungPdfExportieren();
  const loeschen = useExportLoeschen();

  const geschlossene = (abstimmungen.data ?? []).filter(
    (abstimmung) => abstimmung.status === 'geschlossen',
  );
  const standVon = (abstimmungId: string): AbstimmungExportstand | undefined =>
    (staende.data ?? []).find((stand) => stand.abstimmungId === abstimmungId);
  const veranstaltungsexport =
    (exporte.data ?? []).find((eintrag) => eintrag.art === 'veranstaltung_json') ?? null;
  const veranstaltungspdf =
    (exporte.data ?? []).find((eintrag) => eintrag.art === 'veranstaltung_pdf') ?? null;

  const laeuft =
    abstimmungExportieren.isPending ||
    veranstaltungExportieren.isPending ||
    veranstaltungPdfExportieren.isPending ||
    loeschen.isPending;
  const fehler =
    abstimmungExportieren.error ??
    veranstaltungExportieren.error ??
    veranstaltungPdfExportieren.error ??
    loeschen.error ??
    null;

  if (veranstaltungen.isPending) {
    return <p className="text-text-gedaempft text-sm">Wird geladen …</p>;
  }

  if (veranstaltungen.isError) {
    return <Fehlermeldung>{veranstaltungen.error.message}</Fehlermeldung>;
  }

  if (alle.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">Ergebnisse und Exporte</h1>
        <Karte>
          <p className="text-sm">
            Es ist noch keine Veranstaltung angelegt. Ergebnisse entstehen, sobald eine Abstimmung
            geschlossen wurde.
          </p>
        </Karte>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Ergebnisse und Exporte</h1>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Veranstaltung</span>
        <select
          value={veranstaltungId}
          onChange={(ereignis) => {
            setGewaehlteId(ereignis.target.value);
          }}
          className="border-rand bg-flaeche min-h-touchflaeche max-w-xl rounded-md border px-3"
        >
          {alle.map((eintrag) => (
            <option key={eintrag.id} value={eintrag.id}>
              {eintrag.bezeichnung} · {eintrag.code}
            </option>
          ))}
        </select>
      </label>

      {fehler !== null && <Fehlermeldung>{fehler.message}</Fehlermeldung>}

      {zuLoeschen !== null && (
        <BestaetigungsDialog
          titel="Exportdatei löschen?"
          beschreibung={
            <>
              Die Datei <code>{zuLoeschen.dateiname}</code> wird gelöscht. Sie kann jederzeit neu
              erzeugt werden; die frühere Erstellung bleibt vermerkt.
            </>
          }
          bestaetigungstext="Endgültig löschen"
          gefahr
          laeuft={loeschen.isPending}
          onBestaetigen={() => {
            loeschen.mutate(zuLoeschen.id, { onSuccess: () => setZuLoeschen(null) });
          }}
          onAbbrechen={() => {
            setZuLoeschen(null);
          }}
        />
      )}

      <Karte>
        <h2 className="text-base font-semibold">Vollständiger Veranstaltungsexport</h2>
        <p className="text-text-gedaempft mt-1 mb-3 text-sm">
          Alle Abstimmungen mit Regeln, Zeitpunkten und aggregierten Ergebnissen. JSON mit
          UTC-Zeitpunkten für die technische Weiterverarbeitung, PDF mit allen Ergebnissen in
          einer Datei statt je Abstimmung einzeln. Beide ohne Tokens, Teilnahmestatus und
          Sitzungsdaten.
        </p>
        <ExportFormatZeile
          beschriftung="JSON"
          eintrag={veranstaltungsexport}
          laeuft={laeuft}
          onErzeugen={() => {
            veranstaltungExportieren.mutate(veranstaltungId);
          }}
          onLoeschen={setZuLoeschen}
        />
        <ExportFormatZeile
          beschriftung="PDF"
          eintrag={veranstaltungspdf}
          laeuft={laeuft}
          onErzeugen={() => {
            veranstaltungPdfExportieren.mutate(veranstaltungId);
          }}
          onLoeschen={setZuLoeschen}
        />
      </Karte>

      <Karte>
        <h2 className="text-base font-semibold">Geschlossene Abstimmungen</h2>

        {abstimmungen.isPending ? (
          <p className="text-text-gedaempft mt-2 text-sm">Wird geladen …</p>
        ) : geschlossene.length === 0 ? (
          <p className="text-text-gedaempft mt-2 text-sm">
            Diese Veranstaltung hat noch keine geschlossene Abstimmung. Annullierte Abstimmungen
            erhalten kein Ergebnis.
          </p>
        ) : (
          <ul className="mt-3 flex flex-col gap-4">
            {geschlossene.map((abstimmung) => {
              const stand = standVon(abstimmung.id);

              return (
                <li key={abstimmung.id} className="border-rand rounded-lg border p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-medium">
                      <Link
                        to={`/verwaltung/abstimmungen/${abstimmung.id}`}
                        className="hover:text-akzent underline"
                      >
                        Nr. {abstimmung.nummer} · {abstimmung.ueberschrift}
                      </Link>
                    </h3>
                    {/* `role="status"` unterscheidet den Gesamtstand der Abstimmung von den
                        gleichlautenden Angaben je Format — und sagt eine Änderung an (§19.1). */}
                    <span
                      role="status"
                      className={[
                        'text-sm',
                        stand?.exportiert === true ? 'text-text-gedaempft' : 'text-warnung',
                      ].join(' ')}
                    >
                      {stand?.exportiert === true ? 'exportiert' : 'noch nicht exportiert'}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-col">
                    {(['pdf', 'csv', 'json'] as ExportformatWert[]).map((format) => (
                      <ExportFormatZeile
                        key={format}
                        beschriftung={EXPORTFORMAT_BESCHRIFTUNG[format]}
                        eintrag={
                          stand?.formate.find((eintrag) => eintrag.format === format)?.eintrag ??
                          null
                        }
                        laeuft={laeuft}
                        onErzeugen={() => {
                          abstimmungExportieren.mutate({ abstimmungId: abstimmung.id, format });
                        }}
                        onLoeschen={setZuLoeschen}
                      />
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Karte>
    </div>
  );
}
